import React from "react";
import {Badge, TableLink} from "peggirkit";
import {FormattedMessage} from "react-intl";
import {ProductOption} from "@/data/entities/option";
import {formatMoney} from "@/utils/MoneyUtils";
import {OptionGroupsLookup} from "@widgets/menu/optionGroups/optionGroupsLookup";

type Props = {
    options: ProductOption[],
    lookup: OptionGroupsLookup,
    disabled: boolean,
    onRemoveOption: (option: ProductOption, otherGroupCount: number) => void,
};

const thClassName = "px-3 py-3 text-sm font-semibold text-gray-900 dark:text-white";
const tdClassName = "px-3 py-4 text-sm whitespace-nowrap tabular-nums text-gray-500 dark:text-slate-400";

const AvailabilityBadge = ({option}: { option: ProductOption }) => {
    const delivery = option.availability.includes("delivery");
    const takeaway = option.availability.includes("takeaway");

    if (delivery && takeaway) return null;
    if (!delivery && !takeaway) return (
        <Badge size={"xs"} color={"danger"}>
            <FormattedMessage id={"notAvailable"}/>
        </Badge>
    );
    return (
        <Badge size={"xs"} color={"warning"}>
            <FormattedMessage id={delivery ? "onlyDelivery" : "onlyTakeaway"}/>
        </Badge>
    );
};

const OptionsTable = ({options, lookup, disabled, onRemoveOption}: Props) => {
    return (
        <div
            className={
                "overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 " +
                "dark:bg-slate-800 dark:ring-white/10"
            }
        >
            <table className={"min-w-full divide-y divide-gray-200 dark:divide-slate-700"}>
                <thead className={"bg-gray-50 dark:bg-slate-900/40"}>
                <tr>
                    <th scope={"col"} className={`${thClassName} pl-4 sm:pl-6 text-left`}>
                        <FormattedMessage id={"name"}/>
                    </th>
                    <th scope={"col"} className={`${thClassName} hidden sm:table-cell text-right`}>
                        <FormattedMessage id={"priceDelivery"}/>
                    </th>
                    <th scope={"col"} className={`${thClassName} hidden sm:table-cell text-right`}>
                        <FormattedMessage id={"priceTakeaway"}/>
                    </th>
                    <th scope={"col"} className={`${thClassName} hidden md:table-cell text-right`}>
                        <FormattedMessage id={"vat"}/>
                    </th>
                    <th scope={"col"} className={"relative py-3 pl-3 pr-4 sm:pr-6"}>
                            <span className={"sr-only"}>
                                <FormattedMessage id={"actions"}/>
                            </span>
                    </th>
                </tr>
                </thead>

                <tbody className={"divide-y divide-gray-200 dark:divide-slate-700"}>
                {
                    options.length === 0 &&
                    <tr>
                        <td colSpan={5} className={"py-6 px-4 sm:px-6 text-sm text-gray-500 dark:text-slate-400"}>
                            <FormattedMessage id={"noOptionsInGroup"}/>
                        </td>
                    </tr>
                }

                {options.map((option: ProductOption) => {
                    // This group included
                    const otherGroupCount = (lookup.groupCountByOption.get(option.id) ?? 1) - 1;

                    return (
                        <tr key={option.id}>
                            <td className={"py-4 pl-4 pr-3 sm:pl-6 text-sm"}>
                                <div className={"font-medium text-gray-900 dark:text-white"}>
                                    {option.name}
                                </div>

                                {/* Prices on small screens, without the price columns */}
                                <div className={"mt-1 sm:hidden tabular-nums text-gray-500 dark:text-slate-400"}>
                                    {formatMoney(Number(option.price))}
                                    {" · "}
                                    {formatMoney(Number(option.priceTakeaway))}
                                </div>

                                <div className={"mt-1.5 flex flex-wrap gap-1.5 empty:hidden"}>
                                    {
                                        option.soldout &&
                                        <Badge size={"xs"} color={"danger"}>
                                            <FormattedMessage id={"soldOut"}/>
                                        </Badge>
                                    }
                                    <AvailabilityBadge option={option}/>
                                    {
                                        otherGroupCount > 0 &&
                                        <Badge size={"xs"} color={"secondary"}>
                                            <FormattedMessage
                                                id={"alsoInOtherGroups"}
                                                values={{count: otherGroupCount}}
                                            />
                                        </Badge>
                                    }
                                </div>
                            </td>
                            <td className={`${tdClassName} hidden sm:table-cell text-right`}>
                                {formatMoney(Number(option.price))}
                            </td>
                            <td className={`${tdClassName} hidden sm:table-cell text-right`}>
                                {formatMoney(Number(option.priceTakeaway))}
                            </td>
                            <td className={`${tdClassName} hidden md:table-cell text-right`}>
                                {option.vat}%
                            </td>
                            <td className={"py-4 pl-3 pr-4 sm:pr-6 text-right text-sm whitespace-nowrap"}>
                                <TableLink
                                    color={"danger"}
                                    disabled={disabled}
                                    buttonAction={{
                                        as: "button",
                                        actionProps: {
                                            onClick: e => {
                                                e.preventDefault();
                                                onRemoveOption(option, otherGroupCount);
                                            },
                                        },
                                    }}
                                >
                                    <FormattedMessage id={"delete"}/>
                                    <span className={"sr-only"}>, {option.name}</span>
                                </TableLink>
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
};

export default OptionsTable;